/**
 * Contratos zod del endpoint Auth.
 *
 * Mock auth: cualquier password no-vacío es válido si el username existe.
 * En producción Eze conecta con Google Workspace SSO — el contrato es el
 * mismo pero el back valida contra el IdP corporativo.
 */

import { z } from 'zod';
import { UsuarioSchema } from './usuarios';

// --- Login ---
export const LoginRequest = z.object({
  username: z.string().min(1, 'Usuario requerido'),
  password: z.string().min(1, 'Contraseña requerida'),
});

export const LoginResponse = z.object({
  usuario: UsuarioSchema,
  /** ISO timestamp del login — usado como fingerprint de sesión. */
  loginAt: z.string().datetime(),
});

export type LoginRequest = z.infer<typeof LoginRequest>;
export type LoginResponse = z.infer<typeof LoginResponse>;

// --- Login con Google Workspace SSO (paquete AB) ---
//
// En producción este endpoint recibe el `idToken` del flujo GIS (Google
// Identity Services). El backend hace:
//   1. Verificar firma + expiración + audience con google-auth-library.
//   2. Validar `hd` (hosted domain) contra lista permitida (everexpharma.com).
//   3. Buscar Usuario por email (case-insensitive). Si no existe → 403.
//   4. Generar JWT propio + responder { usuario, loginAt }.
//
// Misma shape de `LoginResponse` que el login clásico — los stores no cambian.
export const GoogleLoginRequest = z.object({
  /** JWT ID token emitido por Google Identity Services. */
  idToken: z.string().min(1, 'idToken requerido'),
});
export type GoogleLoginRequest = z.infer<typeof GoogleLoginRequest>;

// --- Reautenticación (step-up auth para acciones sensibles) ---
export const ReauthRequest = z.object({
  password: z.string().min(1, 'Contraseña requerida'),
});

export const ReauthResponse = z.object({
  ok: z.literal(true),
  /** ISO timestamp del reauth — para que el back lo loguee. */
  reauthAt: z.string().datetime(),
});

export type ReauthRequest = z.infer<typeof ReauthRequest>;
export type ReauthResponse = z.infer<typeof ReauthResponse>;

// --- Error genérico ---
export const ApiErrorResponse = z.object({
  error: z.string(),
  code: z.string().optional(),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponse>;
